import type { ResultOf } from '@graphql-typed-document-node/core';
import type { FragmentType } from 'src/gql';
import { Separator } from '@savvy-kit/components/separator';
import { Text } from '@savvy-kit/foundations/text';
import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';
import numeral from 'numeral';
import ItemDetailsRow from 'src/components/portfolio/views/item-details-row';
import { graphql, unmaskFragment } from 'src/gql';

import defaultGuard from '@savvy/utils/default-guard';

export const ViewAssetDialogSecondaryAccountDetailRowsFragment = graphql(/* GraphQL */ `
  fragment ViewAssetDialogSecondaryAccountDetailRows on Asset {
    assetClass
    companyEquityExercisePrice
    companyEquityGrantType
    companyEquitySharesIssued
    companyEquityVestingCliff
    companyEquityVestingDuration
    companyEquityVestingRate
    companyEquityVestingStartDate
    liabilities {
      acquiredAt
      annualInterestPercent
      currentBalance {
        amount
      }
      originalBalance {
        amount
      }
      providerName
      timeToMaturity
    }
  }
`);

type Props = {
  readonly assetFrag: FragmentType<typeof ViewAssetDialogSecondaryAccountDetailRowsFragment>;
};
export function ViewAssetDialogSecondaryAccountDetailRows({ assetFrag }: Props) {
  const asset = unmaskFragment(ViewAssetDialogSecondaryAccountDetailRowsFragment, assetFrag);
  let secondaryContent: null | React.ReactElement = null;
  switch (asset.assetClass) {
    case 'company_equity':
      secondaryContent = <CompanyEquitySecondaryDetailRows asset={asset} />;
      break;
    case 'real_estate':
      secondaryContent = <RealEstateSecondaryDetailRows asset={asset} />;
      break;
    case 'collectible':
    case 'insurance_policy':
    case 'vehicle':
    case 'other':
      break;
    default:
      return defaultGuard(asset.assetClass, null);
  }
  return isNil(secondaryContent) ? null : (
    <>
      <Separator className="bg-border-light" />
      <Text variant="subtitle-lg" color="dark">
        {assetSecondaryHeaderText(asset)}
      </Text>
      <div>{secondaryContent}</div>
    </>
  );
}

function CompanyEquitySecondaryDetailRows({
  asset,
}: {
  readonly asset: ResultOf<typeof ViewAssetDialogSecondaryAccountDetailRowsFragment>;
}) {
  if (!asset.companyEquityGrantType) {
    return <Text>No grant details</Text>;
  }
  return (
    <div>
      <ItemDetailsRow detailLabel="Grant type" value={asset.companyEquityGrantType} />
      <ItemDetailsRow detailLabel="Shares issued" value={asset.companyEquitySharesIssued} />
      {asset.companyEquityExercisePrice && (
        <ItemDetailsRow
          detailLabel="Exercise price"
          value={numeral(asset.companyEquityExercisePrice).format('$0,0.00')}
        />
      )}
      <ItemDetailsRow
        detailLabel="Vesting start date"
        value={DateTime.fromISO(asset.companyEquityVestingStartDate ?? '').toFormat('LLL dd, yyyy')}
      />
      <ItemDetailsRow
        detailLabel="Vesting duration"
        value={
          Number(asset.companyEquityVestingDuration) > 1
            ? `${Number(asset.companyEquityVestingDuration)} years`
            : `${Number(asset.companyEquityVestingDuration)} year`
        }
      />
      <ItemDetailsRow
        detailLabel="Vesting cliff"
        value={
          Number(asset.companyEquityVestingCliff) > 1
            ? `${Number(asset.companyEquityVestingCliff)} years`
            : `${Number(asset.companyEquityVestingCliff)} year`
        }
      />
      <ItemDetailsRow detailLabel="Vesting rate" value={asset.companyEquityVestingRate} />
    </div>
  );
}

function RealEstateSecondaryDetailRows({
  asset,
}: {
  readonly asset: ResultOf<typeof ViewAssetDialogSecondaryAccountDetailRowsFragment>;
}) {
  if (asset.liabilities.length === 0) {
    return <Text>No mortgage details</Text>;
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
  const mortgage = asset.liabilities[0]!;
  return (
    <div>
      <ItemDetailsRow
        detailLabel="Original balance"
        value={numeral(mortgage.originalBalance.amount).format('$0,0.00')}
      />
      <ItemDetailsRow detailLabel="Current balance" value={numeral(mortgage.currentBalance.amount).format('$0,0.00')} />
      <ItemDetailsRow detailLabel="Start date" value={DateTime.fromISO(mortgage.acquiredAt).toFormat('LLL dd, yyyy')} />
      {mortgage.timeToMaturity && (
        <ItemDetailsRow
          detailLabel="Duration"
          value={
            Number(mortgage.timeToMaturity) > 1
              ? `${Number(mortgage.timeToMaturity)} years`
              : `${Number(mortgage.timeToMaturity)} year`
          }
        />
      )}
      {mortgage.annualInterestPercent && (
        <ItemDetailsRow detailLabel="Annual interest rate" value={`${mortgage.annualInterestPercent}%`} />
      )}
      {mortgage.providerName && <ItemDetailsRow detailLabel="Mortgage lender" value={mortgage.providerName} />}
    </div>
  );
}

function assetSecondaryHeaderText(asset: ResultOf<typeof ViewAssetDialogSecondaryAccountDetailRowsFragment>) {
  switch (asset.assetClass) {
    case 'company_equity':
      return 'Grant details';
    case 'real_estate':
      return 'Mortgage details';
    case 'collectible':
    case 'insurance_policy':
    case 'vehicle':
    case 'other':
      return null;
    default:
      return defaultGuard(asset.assetClass, null);
  }
}
